import { NavLink, Outlet } from 'react-router';
import { ROLE_LABELS, type Permission, type SystemRole } from '@aurea/contracts';
import { initials, useAuth } from '../lib/auth';
import { BrandMark } from './ui';
import { NotificationBell } from './NotificationBell';
import { SectionSwitcher } from './SectionSwitcher';
import { ThemeToggle } from './ThemeToggle';

export interface NavItem {
  to: string;
  label: string;
  /** Без права вкладка не показывается. Пусто — видна всем. */
  permission?: Permission;
}

/** Ширина рабочей колонки. Одна на шапку, вкладки и содержимое. */
const COLUMN = 'mx-auto w-full max-w-[1200px] px-7';

/** Общая рамка кабинета: шапка из прототипов плюс вкладки раздела. */
export function AppLayout({ nav }: { nav: NavItem[] }) {
  const { user, logout, can } = useAuth();
  if (!user) return null;

  // Вкладка без права не показывается: раньше её было видно, но переход
  // упирался в отказ — раздел закрывался одним правом на всех.
  const visible = nav.filter((item) => !item.permission || can(item.permission));

  const roleName = ROLE_LABELS[user.role.code as SystemRole] ?? user.role.name;

  return (
    <div className="min-h-full">
      {/*
        Полосы шапки и вкладок тянутся во всю ширину, а их содержимое встаёт
        в ту же колонку, что и рабочая область. Раньше колонку задавал только
        <main>, и на широком экране вкладки уезжали левее содержимого.
      */}
      <header className="sticky top-0 z-20 border-b border-border bg-surface">
        <div className={`${COLUMN} flex h-16 items-center justify-between`}>
          <div className="flex items-center gap-3">
            <BrandMark />
            <SectionSwitcher />
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />

            {/* Уведомления нужны тем, кто ведёт кандидатов, а не самому кандидату. */}
            {user.permissions.includes('candidates.read') && <NotificationBell />}

            <span className="rounded-full bg-accent-dim px-3.5 py-1.5 text-[11.5px] font-bold tracking-wider text-accent uppercase">
              {roleName}
            </span>

            <span
              className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-border bg-accent-soft text-[13px] font-extrabold text-accent"
              title={`${user.lastName} ${user.firstName}`}
            >
              {initials(user)}
            </span>

            <button
              onClick={() => void logout()}
              className="rounded-[9px] border border-border bg-surface-2 px-3 py-2 text-[12.5px] font-bold text-ink-dim hover:bg-border"
            >
              Выйти
            </button>
          </div>
        </div>
      </header>

      {visible.length > 1 && (
        <nav className="border-b border-border bg-surface">
          <div className={`${COLUMN} flex gap-1 overflow-x-auto`}>
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  `shrink-0 border-b-[2.5px] px-4 py-3.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'border-accent text-accent'
                      : 'border-transparent text-ink-faint hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      <main className={`${COLUMN} py-7`}>
        <Outlet />
      </main>
    </div>
  );
}
